import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Switch, Router as WouterRouter, useLocation } from "wouter";
import { ThemeProvider } from "next-themes";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import { Navigation } from "@/components/navigation";
import { CapturePage } from "@/pages/capture";
import { BrowserPage } from "@/pages/browser";
import { PinnedPage } from "@/pages/pinned";
import { SearchPage } from "@/pages/search";
import { AppearancePage } from "@/pages/appearance";
import { ThemesPage } from "@/pages/themes";
import { TasksPage } from "@/pages/tasks";
import { CalendarPage } from "@/pages/calendar";
import { TodayMemoryPage } from "@/pages/today-memory";
import { TrashPage } from "@/pages/trash";
import { ArchivePage } from "@/pages/archive";
import NotFound from "@/pages/not-found";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  const [location, navigate] = useLocation();
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("App")) return;
    let handle: { remove: () => void } | undefined;
    let cancelled = false;
    CapApp.addListener("backButton", ({ canGoBack }) => {
      if (location !== "/") {
        if (canGoBack) window.history.back();
        else navigate("/");
      } else {
        CapApp.exitApp();
      }
    }).then((h) => {
      if (cancelled) h.remove();
      else handle = h;
    });
    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [location, navigate]);
  const [bgHeight, setBgHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const updateHeight = () => setBgHeight(window.innerHeight);
    window.addEventListener("orientationchange", updateHeight);
    return () =>
      window.removeEventListener("orientationchange", updateHeight);
  }, []);
  const hideNav = location.startsWith("/search");
  // Derived directly during render (not via state+effect) so it updates in
  // the same paint as the route change — no leftover frame from the
  // previous page's background while navigating away from "/".
  const customBg =
    location === "/"
      ? localStorage.getItem("rememly-capture-background")
      : null;

  return (
    <div
      className={`min-h-[100dvh] text-foreground flex flex-col font-sans selection:bg-primary/20 ${
        customBg ? "bg-transparent" : "bg-background"
      }`}
    >
      {customBg && (
        <div
          className="fixed inset-x-0 top-0 -z-10 bg-cover bg-center"
          style={{
            backgroundImage: `url(${customBg})`,
            height: `${bgHeight}px`,
          }}
        />
      )}
      {!hideNav && <Navigation />}
      <main className="flex-1">
        <Switch>
          <Route path="/" component={CapturePage} />
          <Route path="/browse/:id?" component={BrowserPage} />
          <Route path="/pinned/:id?" component={PinnedPage} />
          <Route path="/search/:id?" component={SearchPage} />
          <Route path="/tasks/:id?" component={TasksPage} />
          <Route path="/calendar" component={CalendarPage} />
          <Route path="/today-memory" component={TodayMemoryPage} />
          <Route path="/trash" component={TrashPage} />
          <Route path="/archive" component={ArchivePage} />
          <Route path="/appearance" component={AppearancePage} />
          <Route path="/themes" component={ThemesPage} />
          <Route component={NotFound} />
        </Switch>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        themes={["light", "dark", "black"]}
      >
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Router />
        </WouterRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
